import { z } from "zod";

import {
  priceCeilingReadSchema,
  priceCeilingSchema,
  priceLockReadSchema,
  priceLockSchema,
} from "./vehicle-price-contract.js";

/**
 * The catalogue's reads and the dealer's photo doors, **one contract for the doors and for every client**
 * (`GENERIC-DOOR-SWEEP`, Arc 3, A48): `GET /vehicles`, `GET /vehicles/{id}`, `GET /me/vehicles`,
 * `GET /me/vehicles/{id}`, `POST /me/vehicles/{id}/photos` and `GET /me/vehicles/{id}/photos`. The two upload tickets
 * (`…/photos/ticket`, `…/documents/ticket`) answer {@link uploadTicketSchema}, already shared since Arc 1.
 *
 * Until these files client-web declared `{ data?: unknown[] }` and dealer-web `<unknown>` and narrowed by hand, and the
 * API answered `{ data: unknown[] }` or `unknown`. **Prices and kilometres are whole numbers** (`INTEGER` columns): every
 * `*Amount` is `z.number().int()`, so a float or a string where a price stands fails the integration spec that parses the
 * real answer. **A photo's `url` is a presigned GET derived at read time and never stored**: a string, and the door gives
 * no expiry for it (the upload TICKET does, `expiresAt`, in `upload-ticket.ts`).
 */

/** One photograph of a car, as both the public detail and the dealer's gallery list it. */
export const vehiclePhotoSchema = z.strictObject({
  id: z.string(),
  /** The view's name (« Avant droit », « autre »); `null` for a photograph stored without one. */
  view: z.string().nullable(),
  url: z.string(),
  fileName: z.string(),
});

/** A card's distance line, estimated from zone centroids; absent when no origin was named. */
const distanceSchema = z.strictObject({
  kilometres: z.number(),
  minutes: z.number(),
  basis: z.string(),
});

/** One card of the public catalogue (`GET /api/v1/vehicles`). Never the plate, never the buyer. */
export const catalogueCardSchema = z.strictObject({
  id: z.string(),
  title: z.string(),
  year: z.number().int(),
  mileageKm: z.number().int(),
  /** The enum or `null`: the column is nullable (`dto.ts`: `Gearbox | null`). */
  gearbox: z.string().nullable(),
  fuel: z.string().nullable(),
  seats: z.number().int().nullable(),
  city: z.string(),
  district: z.string(),
  priceAmount: z.number().int(),
  stockReference: z.string(),
  sellerName: z.string().nullable(),
  /** Not computed yet: the door answers `null` and a client prints a dash. */
  reliability: z.null(),
  photoCount: z.number().int(),
  photoUrl: z.string().nullable(),
  /** The instalment the card prints, whole francs; `null` when it cannot be quoted. */
  monthlyAmount: z.number().int().nullable(),
  affordable: z.boolean(),
  hasValidReport: z.boolean(),
  engaged: z.boolean(),
  distance: distanceSchema.nullable(),
});
export const catalogueAnswerSchema = z.strictObject({ data: z.array(catalogueCardSchema) });

/** `GET /api/v1/vehicles/{id}`: the public fiche, gallery and seller included. */
export const vehicleDetailAnswerSchema = z.strictObject({
  id: z.string(),
  title: z.string(),
  make: z.string(),
  model: z.string(),
  year: z.number().int(),
  mileageKm: z.number().int(),
  colour: z.string(),
  gearbox: z.string().nullable(),
  fuel: z.string().nullable(),
  seats: z.number().int().nullable(),
  city: z.string(),
  district: z.string(),
  priceAmount: z.number().int(),
  photosCount: z.number().int(),
  hasValidReport: z.boolean(),
  stage: z.string(),
  engaged: z.boolean(),
  photos: z.array(vehiclePhotoSchema),
  seller: z.strictObject({ name: z.string().nullable(), reliability: z.null() }),
});

/** One car of the dealership's own stock (`GET /api/v1/me/vehicles`). */
export const dealerVehicleRowSchema = z.strictObject({
  id: z.string(),
  stockReference: z.string(),
  title: z.string(),
  make: z.string(),
  model: z.string(),
  year: z.number().int(),
  mileageKm: z.number().int(),
  priceAmount: z.number().int(),
  plate: z.string().nullable(),
  colour: z.string(),
  stage: z.string(),
  listedOn: z.string(),
  /** `libre`, `soft` or `ferme`, read with its deadline: a soft lock past its TTL is already `libre`. */
  lockState: z.string(),
  city: z.string(),
  district: z.string(),
  expertiseReportId: z.string().nullable(),
  /** THAT a buyer is on the car, never WHO (06 M3: a dealer is told a first name, not an identifier). */
  hasBuyer: z.boolean(),
  /** The INSTANT of publication, ISO 8601; the screens derive « en ligne » from it. `null` while unpublished. */
  publishedAt: z.string().nullable(),
  photoCount: z.number().int(),
  /** Francs the last price change took off, `null` when the last move was not a drop. */
  priceDrop: z.number().int().nullable(),
  assigneeUserId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  reportVerdict: z.string().nullable(),
});
export const dealerVehiclesAnswerSchema = z.strictObject({
  data: z.array(dealerVehicleRowSchema),
  /** The SERVER's instant: an age is derived from it, never from the browser's clock (A50). */
  asOf: z.string(),
});

/**
 * `GET /api/v1/me/vehicles/{id}`: the dealer's fiche, with its journal. **One definition, two readings**: strict for stubs and the parity spec, and the lenient READ twin {@link dealerVehicleAnswerReadSchema} (extra keys
 * tolerated at every level, every required field and type identical) that `dealer-web` parses the live answer with.
 * - `priceCeiling` (`B3-PRICING-1`): `{ openedAtAmount, dossierReference } | null`. **`null` until `B3-PRICING-2`**: no ceiling is enforced while the price is locked (ruled 1 November). Required in the strict schema, OPTIONAL in the twin.
 * - `priceLock` (`LOCK-STATE`, `P0-CAT-044`): `{ reason: "dossierOuvert" | "envoyeBanque", dossierReference } | null`, from the same predicate as the price door's 409. Required in the strict schema, OPTIONAL in the twin.
 * Both fields are typed (`priceCeilingSchema`, `priceLockSchema` and their read twins): a consumer reading `priceCeiling?.openedAtAmount` compiles.
 */
const dealerVehicleShape = (object: typeof z.strictObject) => ({
  id: z.string(),
  stockReference: z.string(),
  title: z.string(),
  make: z.string(),
  model: z.string(),
  year: z.number().int(),
  mileageKm: z.number().int(),
  priceAmount: z.number().int(),
  plate: z.string().nullable(),
  colour: z.string(),
  /** `null` for a car listed before the question existed: « never asked », not « no ». */
  registrationDeclared: z.boolean().nullable(),
  registrationNumber: z.string().nullable(),
  gearbox: z.string().nullable(),
  fuel: z.string().nullable(),
  seats: z.number().int().nullable(),
  stage: z.string(),
  stageBefore: z.string().nullable(),
  lockState: z.string(),
  listedOn: z.string(),
  city: z.string(),
  district: z.string(),
  equipment: z.array(z.string()),
  publishedAt: z.string().nullable(),
  assigneeUserId: z.string().nullable(),
  assigneeName: z.string().nullable(),
  journal: z.array(object({ body: z.string(), occurredAt: z.string(), isAutomatic: z.boolean() })),
});
export const dealerVehicleAnswerSchema = z.strictObject({
  ...dealerVehicleShape(z.strictObject),
  priceCeiling: priceCeilingSchema.nullable(),
  priceLock: priceLockSchema.nullable(),
  /** `MINIMUM-VEHICLE-PRICE` arc B: the price is below the platform floor in force now (computed on every read; the price is never rewritten). Required strict, OPTIONAL in the twin. */
  belowMinimumPrice: z.boolean(),
});
export const dealerVehicleAnswerReadSchema = z.looseObject({
  ...dealerVehicleShape(z.looseObject),
  priceCeiling: priceCeilingReadSchema.nullable().optional(),
  priceLock: priceLockReadSchema.nullable().optional(),
  belowMinimumPrice: z.boolean().optional(),
});

/** `POST /api/v1/me/vehicles/{id}/photos`: the photograph attached, where it stands. */
export const photoAttachedAnswerSchema = z.strictObject({
  id: z.string(),
  view: z.string(),
  position: z.number().int(),
});

/** `GET /api/v1/me/vehicles/{id}/photos`: the gallery, and the recommended views still missing. */
export const vehiclePhotosAnswerSchema = z.strictObject({
  data: z.array(vehiclePhotoSchema),
  missingRecommended: z.array(z.string()),
});

export type VehiclePhoto = z.infer<typeof vehiclePhotoSchema>;
export type CatalogueCard = z.infer<typeof catalogueCardSchema>;
export type CatalogueAnswer = z.infer<typeof catalogueAnswerSchema>;
export type VehicleDetailAnswer = z.infer<typeof vehicleDetailAnswerSchema>;
export type DealerVehicleRow = z.infer<typeof dealerVehicleRowSchema>;
export type DealerVehiclesAnswer = z.infer<typeof dealerVehiclesAnswerSchema>;
export type DealerVehicleAnswer = z.infer<typeof dealerVehicleAnswerSchema>;
export type PhotoAttachedAnswer = z.infer<typeof photoAttachedAnswerSchema>;
export type VehiclePhotosAnswer = z.infer<typeof vehiclePhotosAnswerSchema>;
